import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, ChangeDetectorRef, SimpleChanges, input, output, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

function noHtmlTagsValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value;
  if (value != null && /[<>]/.test(String(value))) {
    return { htmlTags: true };
  }
  return null;
}
import {
  CommonService,
  NotificationService,
  TimeconversionService,
  ToastMessageService,
} from '@fitness-force/api';
import {
  AutomationEventType,
  CampaignSource,
  OwnershipLevel,
  PushNotificationSendType,
  Roles,
  SendNotificationTo,
  StaffStatus,
  Status,
} from '@fitness-force/enum';
import { ErrorMsgComponent } from '@fitness-force/errors';;
import type { Actions, AutomationActionPayload, AutomationMaster, CampaignActionsGroup, CampaignRequest, ClientById, PushNotificationActionPayload, PushNotificationTemplateMaster, REPORTSTOREINTERFACE, ReportDataForCampaign, Reports, STAFFINTERFACE, ScheduleActionPayload, SourceDataExtractionRules, WhatsAppSenderConfigurationMasterResponse } from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { EditorModule } from 'primeng/editor';
import { FileUploadModule } from 'primeng/fileupload';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { BehaviorSubject, Observable, catchError, distinctUntilChanged, forkJoin, map, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AutomationScheduleTypeComponent } from '@fitness-force/shared';
import { AvatarModule } from 'primeng/avatar';
import { CheckboxModule } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { Store } from '@ngrx/store';
import { STAFF_STATE } from '@fitness-force/state';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CampaignService } from '@fitness-force/api';
import { ImageCropDialogComponent } from '@fitness-force/common/crop';


@Component({
  selector: 'send-push-notification',
  templateUrl: './send-push-notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DialogService],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ToggleSwitchModule,
    InputTextModule,
    DatePickerModule, SelectModule,
    EditorModule,
    ButtonModule,
    ChipModule,
    FileUploadModule,
    ErrorMsgComponent,
    AutomationScheduleTypeComponent,
    AvatarModule,
    CheckboxModule,
    MultiSelectModule,
    NgxFileDropModule,
    TranslateModule,
    NgClass
]
})

export class SendPushNotificationsComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationService);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastMessageService);
  private timeService = inject(TimeconversionService);
  private campaignsService = inject(CampaignService);
  private cd = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private store = inject(Store);
  private commonService = inject(CommonService);
  private dialogService = inject(DialogService);
  private featureFlagService = inject(FeatureFlagService);

  readonly showPopup = input(false, { alias: "visible" });
  readonly assignedToStaffId = input(0, { alias: "staffId" });
  readonly tenantId = input(0);
  readonly franchiseId = input(0);
  readonly totalRecords = input(new BehaviorSubject<number>(0));
  readonly pushNotificationSendType = input<PushNotificationSendType>(PushNotificationSendType.FreeForm);
  readonly columnData = input(new BehaviorSubject<any>([] as [
])); 
  readonly CampaignSource = input<CampaignSource>(CampaignSource.Clients);
  readonly ReportDataForCampaign = input(new BehaviorSubject<ReportDataForCampaign>({} as ReportDataForCampaign));
  readonly selectedAllData = input(new BehaviorSubject<boolean>(false));
  readonly SelectedClient = input(new BehaviorSubject<ClientById>({} as ClientById));
  readonly actionPayload = input(new BehaviorSubject<PushNotificationActionPayload>({} as PushNotificationActionPayload));
  readonly automationMaster = input(new BehaviorSubject<AutomationMaster>({} as AutomationMaster));
  readonly formGroup = input<any>(new BehaviorSubject<any>(null));
  readonly uuid = input<string>("");
  readonly notificationTo = input<SendNotificationTo>(SendNotificationTo.Client);
  readonly ownership = input<OwnershipLevel>(OwnershipLevel.Tenant);
  readonly mode = input<string>("create");
  readonly scheduleInMin = input<number>(0);
  readonly closeSendPushNotificationsPopup = output<any>({ alias: 'closeSendPushNotificationsPopup' });
  readonly HidePopup = output<{
    closePopup: boolean;
    data: any | null;
    form: UntypedFormGroup | null;
    uuid: string;
    notificationTo: SendNotificationTo;
}>({ alias: 'hide' });

  whatsAppFrom: WhatsAppSenderConfigurationMasterResponse[] = [];
  sendPushNotificationForm: UntypedFormGroup = new UntypedFormGroup({});
  selectedReport: REPORTSTOREINTERFACE = {} as REPORTSTOREINTERFACE;
  bodyreadOnly: boolean = false;
  btnLoading: boolean = false;
  pushNotificationTemplateMasters$ = new BehaviorSubject<PushNotificationTemplateMaster[]>([] as PushNotificationTemplateMaster[]);
  pushNotificationTemplateMaster$ = new BehaviorSubject<PushNotificationTemplateMaster | null>({} as PushNotificationTemplateMaster);
  accessedStaffs:STAFFINTERFACE[] =[];
  showCampaign = new BehaviorSubject<boolean>(false);
  showScheduleType = new BehaviorSubject<boolean>(false);
  refCropper?: DynamicDialogRef | null;
  private destroy$ = new Subject<void>();

  constructor() {
    this.sendPushNotificationForm = this.fb.group({
      is_visible: [false, [Validators.required]],
      campaign_name: ['', []],
      campaign_action_date: [new Date(), []],
      campaign_description: ['', []],
      source_date_extraction_rules: [],
      title: [
        '',
        [Validators.required, Validators.maxLength(200), noHtmlTagsValidator],
      ],
      body: [
        '',
        [Validators.required, Validators.maxLength(300), noHtmlTagsValidator],
      ],
      deep_link: ['', []],
      image_base64: ['', []],
      push_notification_template: ['', []],
      is_marketing_message: ['', []],
      automation_timings_type : [''],
      automation_schedule_type : [''],
      automation_timing : [''],
      notify_record_associated_staff:[''],
      notify_additional_staff_to:[]
    });

    if (this.pushNotificationSendType() == PushNotificationSendType.Bulk) {
      this.sendPushNotificationForm.controls['push_notification_template'].addValidators(
        Validators.required
      );
    } else {
      this.sendPushNotificationForm.controls['push_notification_template'].removeValidators(
        Validators.required
      );
    }

  }

  ngOnInit(): void {
    this.store.select(STAFF_STATE).subscribe((staffs: any) => {
      this.accessedStaffs = staffs.staffList.filter((s: STAFFINTERFACE) => 
      (s.role !== Roles.FFAdmin && s.role.toString() !== Roles[Roles.FFAdmin]) &&
      (s.status === StaffStatus.Active || s.status.toString() === StaffStatus[StaffStatus.Active])
    );
    });
    
    // Check feature flag using getFlag$ for proper initialization
    this.featureFlagService.getFlag$('show_campaign')
      .pipe(takeUntil(this.destroy$))
      .subscribe(isEnabled => {
        this.showCampaign.next(isEnabled);
        this.cd.detectChanges();
      });
    
    this.componentInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  componentInit() {
    let forkArray: Observable<any>[] = [];
    const ownership = this.ownership();
    switch (ownership) {
      case OwnershipLevel.Company:
        forkArray = [
          this.notificationService.getPushNotificationTemplatesByOwnership(ownership, 0, 0),
        ];
        break;
      case OwnershipLevel.Franchise:
        forkArray = [
          this.notificationService.getPushNotificationTemplatesByOwnership(ownership, 0, this.franchiseId()),
        ];
        break;
      case OwnershipLevel.Tenant:
        forkArray = [
          this.notificationService.getPushNotificationTemplatesByOwnership(ownership, this.tenantId(), 0),
        ];
        break;
      default:
        forkArray = [
          this.notificationService.getPushNotificationTemplatesByOwnership(ownership, this.tenantId(), 0),
        ];
        break;
    }
    
    forkJoin(forkArray).pipe(
      map(([pushNotificationTemplatesRes]) => {
        const pushNotificationTemplates = pushNotificationTemplatesRes.items as PushNotificationTemplateMaster[] || [];
        let filteredPushNotificationTemplates = pushNotificationTemplates.filter(o => o.event_type !== null);
        
        const pushNotificationSendType = this.pushNotificationSendType();
        if (pushNotificationSendType === PushNotificationSendType.Automation) {
          filteredPushNotificationTemplates = filteredPushNotificationTemplates.filter(o => {
            const automationMaster = this.automationMaster();
            return o.event_type === automationMaster.value.automation_type || o.event_type.toString() === AutomationEventType[automationMaster.value.automation_type];
          });
        } else if (pushNotificationSendType === PushNotificationSendType.Campaign) {

          filteredPushNotificationTemplates = filteredPushNotificationTemplates.filter(o => o.event_type === AutomationEventType.Campaign || o.event_type.toString() === AutomationEventType[AutomationEventType.Campaign]);
        }
        else if (pushNotificationSendType === PushNotificationSendType.FreeForm) {
          filteredPushNotificationTemplates = filteredPushNotificationTemplates.filter(o => o.event_type === AutomationEventType.Campaign || o.event_type.toString() === AutomationEventType[AutomationEventType.Campaign]);
        }


        filteredPushNotificationTemplates = filteredPushNotificationTemplates.filter((o) =>
            o.status === Status.Active ||
            o.status.toString() === Status[Status.Active]
        );
        
        this.pushNotificationTemplateMasters$.next(filteredPushNotificationTemplates);
        const actionPayload = this.actionPayload();
        if (actionPayload && actionPayload.value) {
          this.setInitData();
        }
        else{
          this.showScheduleType.next(true);
        }
      }),
      catchError(error => {
        this.pushNotificationTemplateMasters$.next([]);
        return [];
      })
    ).subscribe();
    this.valueChangeFunction();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to the selectedOption input
    if (changes['formGroup'] && changes['formGroup'].currentValue != null && changes['formGroup'].currentValue.value != null && changes['formGroup'].currentValue != changes['formGroup'].previousValue)
    {
      this.sendPushNotificationForm = changes['formGroup'].currentValue.value as UntypedFormGroup;
      this.sendPushNotificationForm.updateValueAndValidity();
    }

  }
  
  setInitData() {
    const actionPayload = this.actionPayload();
    if (
      actionPayload.value &&
      actionPayload.value.push_notification_template_master_id &&
      actionPayload.value.push_notification_template_master_id > 0
    ) {
      this.SelectedPushNotificationTemplateFormControl.setValue(
        this.pushNotificationTemplateMasters$.value.find(
          (o) =>
            o.id == this.actionPayload().value.push_notification_template_master_id
        )
      );
      this.selectPushNotificationTemplate();
    } else {
      this.BodyFormControl.setValue(actionPayload.value ? actionPayload.value.body : "");
      this.TitleFormControl.setValue(actionPayload.value ? actionPayload.value.title : "");
    }

    this.DeepLinkFormControl.setValue(actionPayload.value ? actionPayload.value.deep_link : "");
    const mode = this.mode();
    if(mode && mode =='update')
      {
       const scheduleType = this.scheduleInMin() > 0 ? 'After' : this.scheduleInMin() < 0 ?  'Before' : 'Immediate';
       const SelectedAutomationTimingType =  this.commonService.formatScheduleTime(this.scheduleInMin(),this.automationMaster().value.automation_type.toString());
       this.SelectedAutomationScheduleTypeFormControl.setValue({key:SelectedAutomationTimingType.key,value:SelectedAutomationTimingType.key});
       this.SelectedAutomationTimingTypeFormControl.setValue(scheduleType);
       this.sendPushNotificationForm.controls['automation_timing'].setValue({key:SelectedAutomationTimingType.name,value:SelectedAutomationTimingType.value});
       this.sendPushNotificationForm.updateValueAndValidity();
       this.cd.detectChanges();

      };
      this.showScheduleType.next(true);
  }

  valueChangeFunction() {
    this.IsVisibleFormControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any) => {
        if (value) {
          this.CampaignNameFormControl.setValidators([Validators.required]);
          this.CampaignDescriptionFormControl.setValidators([Validators.required]);
          this.CampaignActionDateFormControl.setValidators([Validators.required]);
          this.CampaignNameFormControl.updateValueAndValidity();
          this.CampaignDescriptionFormControl.updateValueAndValidity();
          this.CampaignActionDateFormControl.updateValueAndValidity();
        } else {
          this.CampaignNameFormControl.removeValidators([Validators.required]);
          this.CampaignDescriptionFormControl.removeValidators([Validators.required]);
          this.CampaignActionDateFormControl.removeValidators([Validators.required]);
          this.CampaignNameFormControl.updateValueAndValidity();
          this.CampaignDescriptionFormControl.updateValueAndValidity();
          this.CampaignActionDateFormControl.updateValueAndValidity();
        }
        this.sendPushNotificationForm.updateValueAndValidity();
        this.cd.detectChanges();
      });


    this.SelectedPushNotificationTemplateFormControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any) => {
        if (value) {
          this.BodyFormControl.disable();
          this.TitleFormControl.disable();
          this.bodyreadOnly = true;
        } else {
          this.BodyFormControl.enable();
          this.TitleFormControl.enable();
          this.bodyreadOnly = false;
        }
        this.sendPushNotificationForm.updateValueAndValidity();
        this.cd.detectChanges();
      });
  }


  selectPushNotificationTemplate() {
    let service:any;
    const ownership = this.ownership();
    switch (ownership) {
      case OwnershipLevel.Company:
      service = this.notificationService.GetPushNotificationTemplateMasterByOwnership(ownership,this.SelectedPushNotificationTemplateFormControl.value.id,0,0);
        break;
      case OwnershipLevel.Franchise:
        service = this.notificationService.GetPushNotificationTemplateMasterByOwnership(ownership,this.SelectedPushNotificationTemplateFormControl.value.id,this.franchiseId(),0);
        break;
      case OwnershipLevel.Tenant:
        service = this.notificationService.GetPushNotificationTemplateMasterByOwnership(ownership,this.SelectedPushNotificationTemplateFormControl.value.id,0,this.tenantId());
        break;
      default:
        break;
    }

    service.subscribe({
        next: (res:PushNotificationTemplateMaster) => {
          this.pushNotificationTemplateMaster$.next(res);
          this.BodyFormControl.setValue(res.body_primary_language || res.body_secondary_language);
          this.TitleFormControl.setValue(res.name_primary_language || res.name_secondary_language);
        },
        error: (error:any) => {
          this.pushNotificationTemplateMaster$.next(null);
          this.BodyFormControl.setValue(null);
          this.TitleFormControl.setValue(null);
        },
      });
  }

  getClientMobileNos() {
    const pushNotificationSendType = this.pushNotificationSendType();
    if (pushNotificationSendType == PushNotificationSendType.Bulk) {
      let data = this.columnData().getValue();
      data = data.filter(
        (o: { clt_mobile_country_code: string; clt_mobile_no: string }) =>

          o.clt_mobile_country_code !== '0' && o.clt_mobile_no !== '0'
      );
      const uniqueData = data.map(
        (o: any) => o.clt_mobile_country_code + ' ' + o.clt_mobile_no
      );
      return uniqueData.filter(
        (value: any, index: any, self: string | any[]) =>
          self.indexOf(value) === index
      );
    } else if (pushNotificationSendType == PushNotificationSendType.FreeForm) {
      return [
        this.SelectedClient().value.mobile_country_code +
          ' ' +
          this.SelectedClient().value.mobile_number,
      ];
    } else {
      return null;
    }
  }

  getClientIds(): any {
    const pushNotificationSendType = this.pushNotificationSendType();
    if (pushNotificationSendType == PushNotificationSendType.Bulk)
      return this.filterUniqueNumbers(
        this.columnData().getValue().map((o: any) => parseInt(o.clt_id))

      );
    else if (pushNotificationSendType == PushNotificationSendType.FreeForm) {
      return [this.SelectedClient().value.id];
    } else null;

  }

  SaveData() {
    if (this.sendPushNotificationForm.valid) {
      this.btnLoading = true;
      const pushNotificationActionPayload: PushNotificationActionPayload = {} as PushNotificationActionPayload;

      if (this.SelectedPushNotificationTemplateFormControl.value) {
        pushNotificationActionPayload.push_notification_template_master_id = this.SelectedPushNotificationTemplateFormControl.value.id;
        pushNotificationActionPayload.title = this.SelectedPushNotificationTemplateFormControl.value.title;
        pushNotificationActionPayload.body = this.stripHtmlTags(
          this.BodyFormControl.value,
        );
        pushNotificationActionPayload.deep_link = this.DeepLinkFormControl.value;
        pushNotificationActionPayload.image_base64 = this.ImageBase64FormControl.value.replace('data:image/png;base64,', '');
      } else {
        pushNotificationActionPayload.push_notification_template_master_id = 0;
        pushNotificationActionPayload.title = this.stripHtmlTags(
          this.TitleFormControl.value,
        );
        pushNotificationActionPayload.body = this.stripHtmlTags(
          this.BodyFormControl.value,
        );
        pushNotificationActionPayload.deep_link = this.DeepLinkFormControl.value;
        pushNotificationActionPayload.image_base64 = this.ImageBase64FormControl.value.replace('data:image/png;base64,', '');
      }
      //console.log(pushNotificationActionPayload);

      if(this.pushNotificationSendType() == PushNotificationSendType.Automation)
      {
        const actions : Actions= {} as Actions;
        const data :ScheduleActionPayload = {
          schedule_in_min: 0,
          actions: actions,
          action_name: "",
          associated_staff: false,
          staff_ids:[],
          description:""
        };
        data.schedule_in_min= this.SelectedAutomationTimingFormControl.value ?  this.SelectedAutomationTimingFormControl.value.value :0;
        data.action_name = this.SelectedAutomationTimingFormControl.value ?  this.SelectedAutomationTimingFormControl.value.key :  this.SelectedAutomationTimingTypeFormControl.value;
        data.associated_staff = this.SelectedAssociatedStaffFormControl && this.SelectedAssociatedStaffFormControl.value ? this.SelectedAssociatedStaffFormControl.value  : false ;
        data.staff_ids = this.SelectedAdditionalStaffFormControl && this.SelectedAdditionalStaffFormControl.value ? this.SelectedAdditionalStaffFormControl.value.map((staff: any) => staff.id) : [];
        data.actions.email_action_payload = null;
        data.actions.sms_action_payload = null;
        data.actions.push_notification_action_payload = pushNotificationActionPayload;
        data.actions.task_action_payload = null;
        data.description = pushNotificationActionPayload.body;
        this.closeSendPushNotificationsPopup.emit(true);
        this.HidePopup.emit({closePopup:true, data:data,form:this.sendPushNotificationForm, uuid:this.uuid(),notificationTo: this.notificationTo()});
      }
      else{
        this.closeSendPushNotificationsPopup.emit(true);
        this.HidePopup.emit({closePopup:true, data:pushNotificationActionPayload,form:this.sendPushNotificationForm, uuid:this.uuid(),notificationTo: this.notificationTo()});
      }

    } else {
      Object.keys(this.sendPushNotificationForm.controls).forEach((field) => {
        const control = this.sendPushNotificationForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      const firstElementWithError: Element | null =
        document.querySelector('.ng-invalid');
      if (firstElementWithError)
        firstElementWithError.scrollIntoView({ behavior: 'smooth' });
      this.btnLoading = false;
      this.cd.detectChanges();
    }
  }

  SendPushNotification() {
    if (this.sendPushNotificationForm.valid) {
      this.btnLoading = true;
      if (this.pushNotificationSendType() == PushNotificationSendType.Bulk) {
        const payloadRequest: CampaignRequest[] = [] as CampaignRequest[];
        const payload: CampaignRequest = {} as CampaignRequest;
        const formValues = this.sendPushNotificationForm.value;
        payload.campaign_name = formValues['campaign_name'];
        payload.campaign_isvisble = formValues['is_visible'];
        payload.campaign_description = formValues['campaign_description'];
        payload.tenant_id = this.tenantId();
        payload.campaign_ownership = OwnershipLevel.Tenant;
        payload.campaign_franchise = 0;
        payload.actions_group = {} as CampaignActionsGroup;
        const actionGroups: CampaignActionsGroup = {} as CampaignActionsGroup;
        // actionGroups.campaign_action_date = payload.campaign_isvisble
        //   ? this.timeService.ConvertLocalDateTimeToUTC(
        //       formValues['campaign_action_date']
        //     )
        //   : this.getTomorrowDate();
        actionGroups.campaign_action_date = payload.campaign_isvisble ? this.timeService.ConvertLocalDateTimeToUTC(formValues["campaign_action_date"]) :  new Date().toISOString();
        actionGroups.source_data_type = this.CampaignSource();
        const sourceDataExtractionRules: SourceDataExtractionRules =
          {} as SourceDataExtractionRules;
        const CampaignSourceValue = this.CampaignSource();
        if (CampaignSourceValue == CampaignSource.Clients) {
          sourceDataExtractionRules.clients = this.getClientIds();
          sourceDataExtractionRules.reports = null;
          sourceDataExtractionRules.rule = null;
          sourceDataExtractionRules.jsonrule= null;
        } else if (CampaignSourceValue == CampaignSource.Report) {
          const reports: Reports = {} as Reports;
          const selectedReportData = this.ReportDataForCampaign().value;
          reports.payload = selectedReportData.rpt_fltr_payload;
          reports.relative_date =
            selectedReportData.rpt_fltr_relative_date_range;
          reports.report_key = selectedReportData.report_key;
          sourceDataExtractionRules.reports = reports;
          sourceDataExtractionRules.clients = null;
          sourceDataExtractionRules.rule = null;
          sourceDataExtractionRules.jsonrule= null;
        } else {
          sourceDataExtractionRules.clients = null;
          sourceDataExtractionRules.reports = null;
          sourceDataExtractionRules.rule = null;
          sourceDataExtractionRules.jsonrule= null;
        }
        actionGroups.source_data_extraction_rules = sourceDataExtractionRules;
        actionGroups.action_payload = [] as AutomationActionPayload[];
        const actionPayloads: AutomationActionPayload = {
          actions: {
            client_email_action_payload: null,
            client_sms_action_payload: null,
            client_whatsapp_action_payload: null,
            staff_email_action_payload: null,
            staff_sms_action_payload: null,
            staff_whatsapp_action_payload: null,
            staff_task_action_payload: null,
            client_pushnotification_action_payload: null,
            staff_pushnotification_action_payload: null,
            notification_staff: null
          },
          schedule_in_min: 0
        };
        actionPayloads.actions.staff_sms_action_payload= null;
        actionPayloads.actions.client_sms_action_payload= null;
        actionPayloads.actions.staff_email_action_payload= null;
        actionPayloads.actions.client_email_action_payload= null;
        actionPayloads.actions.staff_task_action_payload= null;
        actionPayloads.actions.client_pushnotification_action_payload= null;
        actionPayloads.actions.staff_pushnotification_action_payload= null;
        const pushNotificationActionPayload: PushNotificationActionPayload = {} as PushNotificationActionPayload;
        if (this.SelectedPushNotificationTemplateFormControl.value) {
          pushNotificationActionPayload.push_notification_template_master_id = this.SelectedPushNotificationTemplateFormControl.value.id;
          pushNotificationActionPayload.body = this.BodyFormControl.value;
          pushNotificationActionPayload.title = this.TitleFormControl.value;
          pushNotificationActionPayload.deep_link = this.DeepLinkFormControl.value;
          pushNotificationActionPayload.image_base64 = this.ImageBase64FormControl.value.replace('data:image/png;base64,', '');
        } else {
          pushNotificationActionPayload.push_notification_template_master_id = 0;
          pushNotificationActionPayload.body = this.BodyFormControl.value;
          pushNotificationActionPayload.title = this.TitleFormControl.value;
          pushNotificationActionPayload.deep_link = this.DeepLinkFormControl.value;
          pushNotificationActionPayload.image_base64 = this.ImageBase64FormControl.value.replace('data:image/png;base64,', '');
        }
        actionPayloads.actions.client_pushnotification_action_payload = pushNotificationActionPayload;
        actionPayloads.actions.staff_pushnotification_action_payload = null;
        actionGroups.action_payload.push(actionPayloads);
        payload.actions_group = actionGroups;
        payloadRequest.push(payload);
        this.campaignsService
          .CreateCampaigns(payloadRequest)
          .subscribe({
            next: () => {
              this.translateService
                .get('SEND_WHATSAPP.MSG.WHATSAPP_SEND')
                .subscribe((translation) => {
                  this.toastService.showSuccess(translation);
                });
              this.btnLoading = false;
              this.cd.detectChanges();
              this.closePopupUp();
            },
            error: (error:any) => {
              this.toastService.showError(error);
              this.btnLoading = false;
              this.cd.detectChanges();
            },
          });
      } else {
        let freeFormPayload:any;
        let templatePayload:any;
        let service:any;
        if(this.SelectedPushNotificationTemplateFormControl.value){
          templatePayload = {
            push_notification_template_master_id:this.SelectedPushNotificationTemplateFormControl.value.id,
            body:'',
            title:'',
            deep_link:'',
            image_base64:''
          };
          service =  this.notificationService.SendTemplatedWhatsAppMessage(this.tenantId(),this.getClientIds(),templatePayload);
        }
        else{
          freeFormPayload = {
            push_notification_template_master_id:0,
            body:this.BodyFormControl.value,
            title:this.TitleFormControl.value,
            deep_link:this.DeepLinkFormControl.value,
            image_base64:this.ImageBase64FormControl.value.replace('data:image/png;base64,', ''),
            is_marketing_message: this.sendPushNotificationForm.controls['is_marketing_message'].value || false,
          };
          service =  this.notificationService.SendWhatsAppMessage(this.tenantId(),this.SelectedClient().value.id,freeFormPayload);
        }
        
        service.subscribe({
            next: (res: any) => {
              this.translateService
                .get('SEND_WHATSAPP.MSG.WHATSAPP_SEND')
                .subscribe((translation) => {
                  this.toastService.showSuccess(translation);
                });
              this.btnLoading = false;
              this.closePopupUp();
              this.cd.detectChanges();
            },
            error: (error: any) => {
              if(error.originalError.error &&  Array.isArray(error.originalError.error) && error.originalError.error.length>0)
                {
                  error.originalError.error.forEach((element: any) => {
                    if(element.success)
                      {
                        this.translateService.get('SEND_WHATSAPP.MSG.WHATSAPP_SEND')
                        .subscribe((translation) => {
                          this.toastService.showSuccess(translation);
                        });
                      }
                      else
                      {
                        this.toastService.showErrorMessage(element.error.errorMessage || element.errorMessage);
                      }
                  });
                }
                else{
                  this.toastService.showError(error);
                  this.btnLoading = false;
                  this.cd.detectChanges();
                }
            },
          });
      }
    } else {
      Object.keys(this.sendPushNotificationForm.controls).forEach((field) => {
        const control = this.sendPushNotificationForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      const firstElementWithError: Element | null =
        document.querySelector('.ng-invalid');
      if (firstElementWithError)
        firstElementWithError.scrollIntoView({ behavior: 'smooth' });
      this.btnLoading = false;
      this.cd.detectChanges();
    }
  }

  getTomorrowDate(): string {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowDate = tomorrow.toISOString();
    return tomorrowDate;
  }

  filterUniqueNumbers(array: number[]): number[] {
    // Return unique numbers (deduplicate array)
    return Array.from(new Set(array));
  }

  closePopupUp() {
    this.closeSendPushNotificationsPopup.emit(true);
    this.HidePopup.emit({ closePopup: true, data: this.actionPayload().value,form:this.sendPushNotificationForm,uuid:this.uuid(),notificationTo: this.notificationTo() });
  }

  handleFileSelect(files: NgxFileDropEntry[]) {
    const file: NgxFileDropEntry = files[0];
    if (file.fileEntry.isFile) {
      const fileEntry = file.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        this.showCropper(file);
      });
    }
  }

  showCropper(imageFile: any) {
    const header: string = 'common.IMAGE_CROP.CLIENT_IMAGE';
    this.refCropper = this.dialogService.open(ImageCropDialogComponent, {
      showHeader: false,
      baseZIndex: 10000,
      width: '70%',
      contentStyle: { 'max-height': '500px', 'max-width': '800px' },
      data: { imageFile, header },
    });
    if (this.refCropper) {
    this.refCropper.onClose.subscribe((croppedImage: any) => {
      if (croppedImage) {
        this.ImageBase64FormControl.setValue(croppedImage.image);
        this.ImageBase64FormControl.markAsTouched();
        this.ImageBase64FormControl.updateValueAndValidity();
        this.sendPushNotificationForm.updateValueAndValidity();
        this.cd.detectChanges();
      }
    });
  }
  }


  Updatevalue(event: any) {
    this.cd.detectChanges();
  }

  ngAfterViewInit() {
    this.removeQlImage();
  }

  removeQlImage() {
    const imgButton = document.getElementsByClassName('ql-image');
    for (let i = 0; i < imgButton.length; i++) {
      imgButton[i].remove();
    }

    const cleanButton = document.getElementsByClassName('ql-clean');
    for (let i = 0; i < cleanButton.length; i++) {
      cleanButton[i].remove();
    }

    const codeBlock = document.getElementsByClassName('ql-code-block');
    for (let i = 0; i < codeBlock.length; i++) {
      codeBlock[i].remove();
    }
    const linkButton = document.getElementsByClassName('ql-link');
    for (let i = 0; i < linkButton.length; i++) {
      linkButton[i].remove();
    }
  }

  public get isFormValid() {
    return this.sendPushNotificationForm.valid;
  }


  get IsVisibleFormControl(): UntypedFormControl {
    return this.sendPushNotificationForm.controls['is_visible'] as UntypedFormControl;
  }


  get CampaignNameFormControl(): UntypedFormControl {
    return this.sendPushNotificationForm.controls[
      'campaign_name'
    ] as UntypedFormControl;
  }


  get CampaignDescriptionFormControl(): UntypedFormControl {
    return this.sendPushNotificationForm.controls[
      'campaign_description'
    ] as UntypedFormControl;
  }


  get CampaignActionDateFormControl(): UntypedFormControl {
    return this.sendPushNotificationForm.controls[
      'campaign_action_date'
    ] as UntypedFormControl;
  }


  get BodyFormControl(): UntypedFormControl {
    return this.sendPushNotificationForm.controls['body'] as UntypedFormControl;
  }

  get TitleFormControl(): UntypedFormControl {
    return this.sendPushNotificationForm.controls['title'] as UntypedFormControl;
  }

  get DeepLinkFormControl(): UntypedFormControl {
    return this.sendPushNotificationForm.controls['deep_link'] as UntypedFormControl;
  }

  get ImageBase64FormControl(): UntypedFormControl {
    return this.sendPushNotificationForm.controls['image_base64'] as UntypedFormControl;
  }


  get SelectedPushNotificationTemplateFormControl(): UntypedFormControl {
    return this.sendPushNotificationForm.controls[
      'push_notification_template'
    ] as UntypedFormControl;
  }

  get SelectedAutomationTimingTypeFormControl()
  {
    return this.sendPushNotificationForm.controls['automation_timings_type'] as UntypedFormControl;
  }

  get SelectedAutomationTimingFormControl()
  {
    return this.sendPushNotificationForm.controls['automation_timing'] as UntypedFormControl;
  }

  get SelectedAutomationScheduleTypeFormControl()
  {
    return this.sendPushNotificationForm.controls['automation_schedule_type'] as UntypedFormControl;
  }

  get SelectedAdditionalStaffFormControl()
  {
    return this.sendPushNotificationForm.controls['notify_additional_staff_to'] as UntypedFormControl;
  }

  get SelectedAssociatedStaffFormControl()
  {
    return this.sendPushNotificationForm.controls['notify_record_associated_staff'] as UntypedFormControl;
  }

  public get GetPushNotificationSendType() {
    return PushNotificationSendType;
  }

  private stripHtmlTags(value: string | null | undefined): string {
    if (value == null) {
      return '';
    }
    return String(value).replace(/[<>]/g, '');
  }

  public get SendNotificationTo()
  {
    return SendNotificationTo;
  }

}

